import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useDialers } from '@/features/dialers/api';
import { useGroupDetail, useUpdateGroup } from '../api';
import { buildDialerRowsPayload } from '../payload';
import { groupFormSchema, type GroupFormInput } from '../schema';
import { GroupForm } from './GroupForm';

export function UpdateGroupPage() {
  const navigate = useNavigate();
  const { groupId: groupIdParam } = useParams<{ groupId: string }>();
  const groupId = Number(groupIdParam);

  const detail = useGroupDetail(groupId);
  const dialers = useDialers();
  const update = useUpdateGroup();

  const form = useForm<GroupFormInput>({
    resolver: zodResolver(groupFormSchema),
    mode: 'onTouched',
    defaultValues: { groupName: '', dialerIds: [] },
  });

  useEffect(() => {
    if (!detail.data) return;
    form.reset({
      groupName: detail.data.groupName ?? '',
      dialerIds: (detail.data.dialers ?? []).map((d) => String(d.dialerId)),
    });
  }, [detail.data, form]);

  const onSubmit = (input: GroupFormInput) => {
    const options = (dialers.data ?? []).map((d) => ({
      value: String(d.dialerId),
      label: d.dialerName,
    }));
    update.mutate(
      {
        groupId,
        groupName: input.groupName.trim(),
        dialerRows: buildDialerRowsPayload(input.dialerIds, options),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Group updated');
          navigate('/group-list');
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Could not update the group.'),
      },
    );
  };

  return (
    <GroupForm
      mode="update"
      form={form}
      onSubmit={onSubmit}
      isPending={update.isPending}
      pageTitle="Update Group"
      pageDescription={
        detail.data ? `Editing "${detail.data.groupName}".` : 'Loading group details…'
      }
    />
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
